import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';

import { MessstelleKategorie } from '../../../Models/messstelle.model';

/**
 * Woher die Vorschau gerade kommt – entscheidet, wann sie wieder zugeht.
 *
 * - `hover`:       Maus über einer Option. Geht beim Verlassen von selbst zu.
 * - `tastatur`:    Pfeiltasten im offenen Dropdown. Folgt der aktiven Option.
 * - `option-info`: Info-Symbol in einer Option angetippt (Touch). Bleibt, bis
 *                  das Dropdown zugeht oder außerhalb geklickt wird.
 * - `suffix`:      Info-Knopf neben dem Feld. Unabhängig vom Dropdown; geht bei
 *                  Klick außerhalb, Escape oder erneutem Klick zu.
 */
type VorschauQuelle = 'hover' | 'tastatur' | 'option-info' | 'suffix';

/**
 * Kategorieauswahl für eine Messstelle.
 *
 * Ein `mat-select`, dessen Optionen Name UND Beschreibung zeigen und zu dem es
 * eine Vorschau der Prüfstruktur (Abschnitte → Prüfpunkte) gibt: beim
 * Überfahren einer Option, beim Blättern mit den Pfeiltasten, per Info-Symbol
 * in der Option (Touch) und per Info-Knopf neben dem Feld für die gewählte
 * Kategorie.
 *
 * Die Vorschau braucht keinen eigenen Abruf – die Struktur steckt in den
 * übergebenen Kategorien. Damit funktioniert sie online wie offline und ohne
 * Admin-Rechte.
 *
 * Verwendung wahlweise mit `[(ngModel)]` (ControlValueAccessor) oder mit
 * `[value]`/`(valueChange)`. Eine Kopie des Markups in jeder Seite wäre eine
 * Stelle mehr, an der die Vorschau auseinanderlaufen kann.
 */
@Component({
  selector: 'app-kategorie-auswahl',
  templateUrl: './kategorie-auswahl.component.html',
  styleUrls: ['./kategorie-auswahl.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KategorieAuswahlComponent),
      multi: true
    }
  ]
})
export class KategorieAuswahlComponent implements ControlValueAccessor, OnDestroy {

  private static naechsteId = 0;

  /** Verzögerungen, damit die Vorschau beim Überstreichen der Liste nicht flackert. */
  private static readonly OEFFNEN_MS = 140;
  private static readonly SCHLIESSEN_MS = 180;

  /** Die auswählbaren Kategorien, samt Struktur für die Vorschau. */
  @Input({ required: true }) kategorien: MessstelleKategorie[] = [];

  @Input() label = 'Kategorie';

  /** Text der leeren Option (Wert `null`). */
  @Input() leerText = '— keine —';

  @Input() hint: string | null = null;

  @Input() appearance: MatFormFieldAppearance = 'outline';

  @Input() disabled = false;

  /** Gewählte Kategorie-Id; `null` = keine. */
  @Input() value: number | null = null;

  @Output() valueChange = new EventEmitter<number | null>();

  @ViewChild(MatSelect) select?: MatSelect;

  readonly vorschauId = `kategorie-vorschau-${KategorieAuswahlComponent.naechsteId++}`;

  // ── Vorschau ──

  vorschauKategorie: MessstelleKategorie | null = null;
  vorschauOrigin: Element | null = null;
  vorschauQuelle: VorschauQuelle | null = null;

  private oeffnenTimer: ReturnType<typeof setTimeout> | null = null;
  private schliessenTimer: ReturnType<typeof setTimeout> | null = null;
  private panelScrollZiel: HTMLElement | null = null;

  /**
   * Nur Geräte mit echtem Hover bekommen die Maus-Vorschau. Auf Touch-Geräten
   * würde das nachgebildete mouseenter beim Antippen kurz aufblitzen; dort
   * gibt es stattdessen das Info-Symbol.
   */
  private readonly hoverMoeglich =
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  private onChange: (wert: number | null) => void = () => { };
  onTouched: () => void = () => { };

  constructor(private host: ElementRef<HTMLElement>) { }

  ngOnDestroy(): void {
    this.timerLoeschen();
    this.panelScrollAbmelden();
  }

  // ========================================================
  // AUSWAHL
  // ========================================================

  get gewaehlt(): MessstelleKategorie | null {
    if (this.value == null) {
      return null;
    }
    return this.kategorien.find(k => k.id === this.value) ?? null;
  }

  get hostElement(): Element {
    return this.host.nativeElement;
  }

  onAuswahl(wert: number | null): void {
    const neu = wert ?? null;
    this.value = neu;
    this.onChange(neu);
    this.valueChange.emit(neu);

    // Die Vorschau galt der alten Auswahl bzw. der überfahrenen Option.
    this.vorschauSchliessen();
  }

  // ── ControlValueAccessor ──

  writeValue(wert: number | null | undefined): void {
    this.value = wert ?? null;
  }

  registerOnChange(fn: (wert: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  // ========================================================
  // VORSCHAU – ZUSTAND
  // ========================================================

  get vorschauOffen(): boolean {
    return this.vorschauKategorie != null && this.vorschauOrigin != null;
  }

  get vorschauFixiert(): boolean {
    return this.vorschauQuelle === 'option-info' || this.vorschauQuelle === 'suffix';
  }

  /**
   * Bevorzugte Lage: neben der Option (rechts, sonst links), beim Info-Knopf
   * unterhalb. Passt nichts, schiebt der Overlay-Dienst in den Sichtbereich
   * (`push`) – abgeschnitten wird nie.
   */
  get positionen(): ConnectedPosition[] {
    if (this.vorschauQuelle === 'suffix') {
      return [
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 }
      ];
    }

    return [
      { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8 },
      { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -8 },
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
    ];
  }

  private vorschauZeigen(
    kategorie: MessstelleKategorie,
    origin: Element,
    quelle: VorschauQuelle
  ): void {
    this.timerLoeschen();
    this.vorschauKategorie = kategorie;
    this.vorschauOrigin = origin;
    this.vorschauQuelle = quelle;
  }

  vorschauSchliessen(): void {
    this.timerLoeschen();
    this.vorschauKategorie = null;
    this.vorschauQuelle = null;
    // Der Ursprung bleibt stehen: das Overlay braucht beim Abbau noch einen.
  }

  private vorschauVerzoegertSchliessen(): void {
    if (this.schliessenTimer) {
      clearTimeout(this.schliessenTimer);
    }
    this.schliessenTimer = setTimeout(
      () => this.vorschauSchliessen(),
      KategorieAuswahlComponent.SCHLIESSEN_MS
    );
  }

  private timerLoeschen(): void {
    if (this.oeffnenTimer) {
      clearTimeout(this.oeffnenTimer);
      this.oeffnenTimer = null;
    }
    if (this.schliessenTimer) {
      clearTimeout(this.schliessenTimer);
      this.schliessenTimer = null;
    }
  }

  // ========================================================
  // VORSCHAU – MAUS
  // ========================================================

  onOptionHover(kategorie: MessstelleKategorie, event: MouseEvent): void {
    if (!this.hoverMoeglich || this.vorschauFixiert) {
      return;
    }

    const origin = event.currentTarget as Element | null;
    if (!origin) {
      return;
    }

    this.timerLoeschen();
    this.oeffnenTimer = setTimeout(
      () => this.vorschauZeigen(kategorie, origin, 'hover'),
      KategorieAuswahlComponent.OEFFNEN_MS
    );
  }

  onOptionVerlassen(): void {
    if (this.oeffnenTimer) {
      clearTimeout(this.oeffnenTimer);
      this.oeffnenTimer = null;
    }

    if (this.vorschauQuelle === 'hover') {
      this.vorschauVerzoegertSchliessen();
    }
  }

  /** Die Maus ist in der Vorschau angekommen – sie darf dort scrollen. */
  onVorschauHover(): void {
    if (this.schliessenTimer) {
      clearTimeout(this.schliessenTimer);
      this.schliessenTimer = null;
    }
  }

  onVorschauVerlassen(): void {
    if (this.vorschauQuelle === 'hover') {
      this.vorschauVerzoegertSchliessen();
    }
  }

  /**
   * Leere Option überfahren: dort gibt es nichts vorzuschauen, eine noch
   * offene Hover-Vorschau geht zu.
   */
  onLeerOptionHover(): void {
    if (this.oeffnenTimer) {
      clearTimeout(this.oeffnenTimer);
      this.oeffnenTimer = null;
    }
    if (this.vorschauQuelle === 'hover' || this.vorschauQuelle === 'tastatur') {
      this.vorschauSchliessen();
    }
  }

  // ========================================================
  // VORSCHAU – INFO-SYMBOLE (TOUCH, TASTATUR)
  // ========================================================

  /**
   * Info-Symbol in einer Option. Darf die Option NICHT auswählen – deshalb
   * wird das Ereignis hier gestoppt, bevor es die Option erreicht.
   */
  onOptionInfoKlick(event: Event, kategorie: MessstelleKategorie): void {
    event.preventDefault();
    event.stopPropagation();

    const option = (event.currentTarget as Element | null)?.closest('.mat-mdc-option') ?? null;
    if (!option) {
      return;
    }

    const schonOffen =
      this.vorschauFixiert && this.vorschauKategorie?.id === kategorie.id;

    if (schonOffen) {
      this.vorschauSchliessen();
    } else {
      this.vorschauZeigen(kategorie, option, 'option-info');
    }
  }

  /** Mousedown auf dem Info-Symbol soll den Fokus im Dropdown lassen. */
  onOptionInfoMousedown(event: Event): void {
    event.preventDefault();
  }

  /** Info-Knopf neben dem Feld: Vorschau der gewählten Kategorie an/aus. */
  onSuffixKlick(event: Event): void {
    // Sonst öffnet der Klick auf den Feldrahmen zugleich das Dropdown.
    event.stopPropagation();

    const kategorie = this.gewaehlt;
    const origin = event.currentTarget as Element | null;

    if (!kategorie || !origin) {
      return;
    }

    if (this.vorschauQuelle === 'suffix') {
      this.vorschauSchliessen();
    } else {
      this.vorschauZeigen(kategorie, origin, 'suffix');
    }
  }

  // ========================================================
  // VORSCHAU – DROPDOWN UND TASTATUR
  // ========================================================

  onPanelGeoeffnet(offen: boolean): void {
    if (offen) {
      // Beim Scrollen der Optionsliste stimmt die Lage der Vorschau nicht
      // mehr – lieber zu als neben der falschen Option.
      setTimeout(() => this.panelScrollAnmelden());
      return;
    }

    this.panelScrollAbmelden();

    if (this.vorschauQuelle !== 'suffix') {
      this.vorschauSchliessen();
    }
  }

  /**
   * Pfeiltasten im offenen Dropdown: die Vorschau folgt der aktiven Option.
   * Das Dropdown hat seine Taste zu diesem Zeitpunkt noch nicht verarbeitet,
   * deshalb erst im nächsten Durchlauf nachsehen.
   */
  onTastatur(event: KeyboardEvent): void {
    const select = this.select;
    if (!select?.panelOpen) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
      case 'End':
      case 'PageDown':
      case 'PageUp':
        setTimeout(() => this.vorschauZurAktivenOption());
        break;
      default:
        break;
    }
  }

  private vorschauZurAktivenOption(): void {
    const select = this.select;
    if (!select?.panelOpen || this.vorschauFixiert) {
      return;
    }

    const aktiv = select.options.find(o => o.active);
    const kategorie = aktiv
      ? this.kategorien.find(k => k.id === aktiv.value) ?? null
      : null;

    if (!aktiv || !kategorie) {
      if (this.vorschauQuelle === 'tastatur' || this.vorschauQuelle === 'hover') {
        this.vorschauSchliessen();
      }
      return;
    }

    const element = typeof document !== 'undefined'
      ? document.getElementById(aktiv.id)
      : null;

    if (element) {
      this.vorschauZeigen(kategorie, element, 'tastatur');
    }
  }

  private readonly beiPanelScroll = (): void => {
    if (!this.vorschauFixiert) {
      this.vorschauSchliessen();
    }
  };

  private panelScrollAnmelden(): void {
    this.panelScrollAbmelden();

    const panel = this.select?.panel?.nativeElement as HTMLElement | undefined;
    if (!panel) {
      return;
    }

    panel.addEventListener('scroll', this.beiPanelScroll, { passive: true });
    this.panelScrollZiel = panel;
  }

  private panelScrollAbmelden(): void {
    if (this.panelScrollZiel) {
      this.panelScrollZiel.removeEventListener('scroll', this.beiPanelScroll);
      this.panelScrollZiel = null;
    }
  }

  // ========================================================
  // VORSCHAU – SCHLIESSEN VON AUSSEN
  // ========================================================

  /** Klick außerhalb der Vorschau: fixierte Vorschau zu, Hover-Vorschau ohnehin. */
  onAussenKlick(event: MouseEvent): void {
    const ziel = event.target as Node | null;

    // Ein Klick auf das Info-Symbol selbst wird dort behandelt (umschalten).
    if (ziel && this.vorschauOrigin?.contains(ziel)) {
      return;
    }

    this.vorschauSchliessen();
  }

  /** Das Overlay wurde abgebaut (z. B. durch Escape) – Zustand nachziehen. */
  onVorschauAbgebaut(): void {
    if (this.vorschauOffen) {
      this.vorschauSchliessen();
    }
  }

  trackById(_index: number, kategorie: MessstelleKategorie): number {
    return kategorie.id;
  }
}
